import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import '../App.css';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [prevPost, setPrevPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        // Buscar post por slug usando el servicio optimizado
        const { getPostBySlug, getPublishedPosts } = require('../services/blogService');
        const foundPost = await getPostBySlug(slug);
        
        if (foundPost) {
          // Obtener todos los posts para encontrar relacionados
          const allPosts = await getPublishedPosts();
        
          setPost(foundPost);
          
          // Buscar posts relacionados
          const related = allPosts
            .filter(p => p.id !== foundPost.id)
            .filter(p => 
              p.category === foundPost.category || 
              (foundPost.tags && p.tags && p.tags.some(tag => foundPost.tags.includes(tag)))
            )
            .slice(0, 3);
          setRelatedPosts(related);

          // Buscar post anterior y siguiente (por fecha)
          const sortedPosts = allPosts.sort((a, b) => 
            new Date(b.createdAt?.toDate?.() || b.createdAt) - new Date(a.createdAt?.toDate?.() || a.createdAt)
          );
          
          const currentIndex = sortedPosts.findIndex(p => p.id === foundPost.id);
          
          if (currentIndex > 0) {
            setNextPost(sortedPosts[currentIndex - 1]); // Post más reciente
          }
          
          if (currentIndex < sortedPosts.length - 1) {
            setPrevPost(sortedPosts[currentIndex + 1]); // Post más antiguo
          }
        } else {
          setError('Post no encontrado');
        }
      } catch (error) {
        console.error('Error loading post:', error);
        setError('Error al cargar el post');
      } finally {
        setLoading(false);
      }
    };
    
    loadPost();
  }, [slug]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a' }}>
        <Header />
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
          <p style={{ color: '#94a3b8', textAlign: 'center' }}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a' }}>
        <Header />
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '1rem',
            border: '1px solid rgba(148, 163, 184, 0.2)'
          }}>
            <h1 style={{ color: '#f1f5f9', marginBottom: '1rem' }}>
              {error || 'Post no encontrado'}
            </h1>
            <Link to="/blog">
              <button style={{
                padding: '0.75rem 2rem',
                borderRadius: '0.5rem',
                border: 'none',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '1rem'
              }}>
                ← Volver al Blog
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a' }}>
      <Header />
      
      <article style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ marginBottom: '2rem' }}>
          <Link to="/blog">
            <button style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              cursor: 'pointer',
              marginBottom: '2rem'
            }}>
              ← Volver al Blog
            </button>
          </Link>
        </div>

        {post.featuredImage && (
          <div style={{
            width: '100%',
            maxHeight: '500px',
            overflow: 'hidden',
            borderRadius: '1rem',
            marginBottom: '2rem',
            border: '1px solid rgba(148, 163, 184, 0.2)'
          }}>
            <img 
              src={post.featuredImage} 
              alt={post.title}
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'cover'
              }}
            />
          </div>
        )}

        <header style={{ marginBottom: '2rem' }}>
          {post.category && (
            <span style={{
              display: 'inline-block',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              background: 'rgba(59, 130, 246, 0.2)',
              color: '#93c5fd',
              marginBottom: '1rem'
            }}>
              {post.category}
            </span>
          )}
          
          <h1 style={{ 
            fontSize: '3rem', 
            color: '#f1f5f9', 
            marginBottom: '1rem',
            lineHeight: '1.2'
          }}>
            {post.title}
          </h1>
          
          {post.excerpt && (
            <p style={{ 
              fontSize: '1.25rem',
              color: '#94a3b8',
              marginBottom: '1rem',
              fontStyle: 'italic'
            }}>
              {post.excerpt}
            </p>
          )}
          
          <div style={{ 
            display: 'flex', 
            gap: '1rem',
            alignItems: 'center',
            color: '#64748b',
            fontSize: '0.875rem',
            paddingTop: '1rem',
            borderTop: '1px solid rgba(148, 163, 184, 0.2)'
          }}>
            <span>📅 {formatDate(post.createdAt)}</span>
            {post.tags && post.tags.length > 0 && (
              <>
                <span>•</span>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {post.tags.map((tag, idx) => (
                    <span key={idx}>#{tag}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        </header>

        {/* Información adicional del post */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ color: '#64748b', fontSize: '0.9rem' }}>
              📖 {Math.max(1, Math.ceil(post.content.split(' ').length / 200))} min lectura
            </span>
            <span style={{ color: '#64748b', fontSize: '0.9rem' }}>
              📝 {post.content.split(' ').length} palabras
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Compartir:</span>
            <button
              onClick={() => navigator.clipboard.writeText(window.location.href)}
              style={{
                padding: '0.5rem',
                borderRadius: '0.25rem',
                border: 'none',
                background: 'rgba(59, 130, 246, 0.2)',
                color: '#93c5fd',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              📋 Copiar enlace
            </button>
          </div>
        </div>

        {/* Contenido del post */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem'
        }}>
          <div 
            style={{ 
              color: '#cbd5e1',
              fontSize: '1.1rem',
              lineHeight: '1.8',
              whiteSpace: 'pre-wrap'
            }}
            dangerouslySetInnerHTML={{ 
              __html: post.content
                .replace(/\n\n/g, '</p><p style="margin: 1.5rem 0;">')
                .replace(/\n/g, '<br/>')
                .replace(/^/, '<p style="margin: 1.5rem 0;">')
                .replace(/$/, '</p>')
                .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #f1f5f9;">$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
            }}
          />
        </div>

        {/* Posts relacionados */}
        {relatedPosts.length > 0 && (
          <div style={{
            background: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '1rem',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            padding: '2rem',
            marginTop: '3rem'
          }}>
            <h3 style={{ 
              color: '#f1f5f9', 
              fontSize: '1.5rem', 
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              📚 Posts Relacionados
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1rem'
            }}>
              {relatedPosts.map(relatedPost => (
                <Link 
                  key={relatedPost.id} 
                  to={`/blog/${relatedPost.slug}`}
                  style={{ textDecoration: 'none' }}
                >
                  <div style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    borderRadius: '0.5rem',
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                    padding: '1rem',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    height: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}>
                    {relatedPost.category && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: '#93c5fd',
                        marginBottom: '0.5rem'
                      }}>
                        {relatedPost.category}
                      </span>
                    )}
                    <h4 style={{ 
                      color: '#f1f5f9', 
                      fontSize: '1rem', 
                      marginBottom: '0.5rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {relatedPost.title}
                    </h4>
                    {relatedPost.excerpt && (
                      <p style={{ 
                        color: '#94a3b8', 
                        fontSize: '0.8rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {relatedPost.excerpt}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Navegación entre posts */}
        {(prevPost || nextPost) && (
          <div style={{
            background: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '1rem',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            padding: '2rem',
            marginTop: '3rem'
          }}>
            <h3 style={{ 
              color: '#f1f5f9', 
              fontSize: '1.2rem', 
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              🔄 Navegación entre Posts
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: prevPost && nextPost ? '1fr 1fr' : '1fr',
              gap: '1rem'
            }}>
              {/* Post Anterior */}
              {prevPost && (
                <Link to={`/blog/${prevPost.slug}`} style={{ textDecoration: 'none' }}>
                  <div style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    borderRadius: '0.5rem',
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                    padding: '1.5rem',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    height: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                      color: '#64748b',
                      fontSize: '0.8rem'
                    }}>
                      <span>←</span>
                      <span>POST ANTERIOR</span>
                    </div>
                    
                    {prevPost.category && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: '#93c5fd',
                        marginBottom: '0.5rem'
                      }}>
                        {prevPost.category}
                      </span>
                    )}
                    
                    <h4 style={{ 
                      color: '#f1f5f9', 
                      fontSize: '1rem', 
                      marginBottom: '0.5rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {prevPost.title}
                    </h4>
                    
                    {prevPost.excerpt && (
                      <p style={{ 
                        color: '#94a3b8', 
                        fontSize: '0.8rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {prevPost.excerpt}
                      </p>
                    )}
                  </div>
                </Link>
              )}

              {/* Post Siguiente */}
              {nextPost && (
                <Link to={`/blog/${nextPost.slug}`} style={{ textDecoration: 'none' }}>
                  <div style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    borderRadius: '0.5rem',
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                    padding: '1.5rem',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    height: '100%',
                    textAlign: nextPost && !prevPost ? 'left' : 'right'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                      color: '#64748b',
                      fontSize: '0.8rem',
                      justifyContent: nextPost && !prevPost ? 'flex-start' : 'flex-end'
                    }}>
                      <span>POST SIGUIENTE</span>
                      <span>→</span>
                    </div>
                    
                    {nextPost.category && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: '#93c5fd',
                        marginBottom: '0.5rem'
                      }}>
                        {nextPost.category}
                      </span>
                    )}
                    
                    <h4 style={{ 
                      color: '#f1f5f9', 
                      fontSize: '1rem', 
                      marginBottom: '0.5rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {nextPost.title}
                    </h4>
                    
                    {nextPost.excerpt && (
                      <p style={{ 
                        color: '#94a3b8', 
                        fontSize: '0.8rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {nextPost.excerpt}
                      </p>
                    )}
                  </div>
                </Link>
              )}
            </div>
          </div>
        )}

        <div style={{ 
          marginTop: '3rem',
          padding: '2rem',
          background: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          textAlign: 'center'
        }}>
          <Link to="/blog">
            <button style={{
              padding: '0.75rem 2rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}>
              ← Ver más posts
            </button>
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
